import {AfterViewInit, Directive, ElementRef, OnDestroy} from '@angular/core';

/**
 * Cria uma barra de rolagem horizontal "falsa", fixada na viewport (não no
 * container da tabela), sincronizada com o scrollLeft real. Resolve o
 * problema de tabelas largas cuja barra nativa só aparece se o usuário
 * rolar até o fundo físico do conteúdo — aqui ela fica sempre visível no
 * limite inferior da tela, enquanto o container estiver ao menos
 * parcialmente visível.
 *
 * O arraste é implementado manualmente (pointerdown/pointermove/pointerup)
 * em vez de depender da rolagem nativa de uma div escondida, que se mostrou
 * pouco confiável para arrastar com o mouse entre navegadores.
 */
@Directive({
  selector: '[appStickyHorizontalScroll]',
  standalone: true
})
export class StickyHorizontalScrollDirective implements AfterViewInit, OnDestroy {
  private track!: HTMLDivElement;
  private thumb!: HTMLDivElement;
  private scrollTarget: HTMLElement | null = null;
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private detectAttempts = 0;

  private dragging = false;
  private dragStartX = 0;
  private dragStartScrollLeft = 0;

  private readonly onWindowScrollOrResize = () => this.updatePosition();

  private readonly onTableScroll = () => this.updateThumb();

  private readonly onThumbPointerDown = (event: PointerEvent) => {
    if (!this.scrollTarget) return;
    this.dragging = true;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.scrollTarget.scrollLeft;
    this.thumb.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  private readonly onThumbPointerMove = (event: PointerEvent) => {
    if (!this.dragging || !this.scrollTarget) return;
    const trackWidth = this.track.clientWidth;
    const maxScroll = this.scrollTarget.scrollWidth - this.scrollTarget.clientWidth;
    const thumbWidth = this.thumb.offsetWidth;
    const maxThumbTravel = Math.max(trackWidth - thumbWidth, 1);

    const deltaX = event.clientX - this.dragStartX;
    const deltaScroll = (deltaX / maxThumbTravel) * maxScroll;

    this.scrollTarget.scrollLeft = this.clamp(this.dragStartScrollLeft + deltaScroll, 0, maxScroll);
  };

  private readonly onThumbPointerUp = (event: PointerEvent) => {
    this.dragging = false;
    if (this.thumb.hasPointerCapture(event.pointerId)) {
      this.thumb.releasePointerCapture(event.pointerId);
    }
  };

  private readonly onTrackPointerDown = (event: PointerEvent) => {
    if (event.target === this.thumb || !this.scrollTarget) return;
    const trackRect = this.track.getBoundingClientRect();
    const clickX = event.clientX - trackRect.left;
    const thumbWidth = this.thumb.offsetWidth;
    const targetThumbLeft = this.clamp(clickX - thumbWidth / 2, 0, Math.max(this.track.clientWidth - thumbWidth, 1));
    const maxThumbTravel = Math.max(this.track.clientWidth - thumbWidth, 1);
    const maxScroll = this.scrollTarget.scrollWidth - this.scrollTarget.clientWidth;

    this.scrollTarget.scrollLeft = this.clamp((targetThumbLeft / maxThumbTravel) * maxScroll, 0, maxScroll);
  };

  constructor(private el: ElementRef<HTMLElement>) {
  }

  ngAfterViewInit(): void {
    this.track = document.createElement('div');
    this.track.className = 'sticky-table-x-scroll';

    this.thumb = document.createElement('div');
    this.thumb.className = 'sticky-table-x-scroll-thumb';
    this.track.appendChild(this.thumb);

    document.body.appendChild(this.track);

    this.thumb.addEventListener('pointerdown', this.onThumbPointerDown);
    this.thumb.addEventListener('pointermove', this.onThumbPointerMove);
    this.thumb.addEventListener('pointerup', this.onThumbPointerUp);
    this.thumb.addEventListener('pointercancel', this.onThumbPointerUp);
    this.track.addEventListener('pointerdown', this.onTrackPointerDown);

    window.addEventListener('scroll', this.onWindowScrollOrResize, true);
    window.addEventListener('resize', this.onWindowScrollOrResize);

    this.resizeObserver = new ResizeObserver(() => this.updatePosition());
    this.resizeObserver.observe(this.el.nativeElement);

    // A p-table recria/reaproveita sua estrutura interna ao trocar modo
    // (paginado <-> rolagem virtual). As vezes o wrapper antigo continua
    // conectado ao DOM mas deixa de ter overflow real (ou vice-versa), entao
    // nao basta checar isConnected — valida se ele ainda "estoura" de verdade.
    this.mutationObserver = new MutationObserver(() => {
      const aindaValido = !!this.scrollTarget
        && this.scrollTarget.isConnected
        && this.scrollTarget.scrollWidth > this.scrollTarget.clientWidth + 1;

      if (!aindaValido) {
        this.detachScrollTarget();
        this.detectAttempts = 0;
        this.tryAttachToScrollTarget();
      }
    });
    this.mutationObserver.observe(this.el.nativeElement, {childList: true, subtree: true});

    this.tryAttachToScrollTarget();
  }

  private tryAttachToScrollTarget(): void {
    const target = this.findScrollableChild(this.el.nativeElement);

    if (!target) {
      if (this.detectAttempts++ < 30) {
        setTimeout(() => this.tryAttachToScrollTarget(), 100);
      }
      return;
    }

    this.scrollTarget = target;
    this.scrollTarget.addEventListener('scroll', this.onTableScroll);
    this.resizeObserver?.observe(this.scrollTarget);
    this.updatePosition();
  }

  private detachScrollTarget(): void {
    this.scrollTarget?.removeEventListener('scroll', this.onTableScroll);
    if (this.scrollTarget) {
      this.resizeObserver?.unobserve(this.scrollTarget);
    }
    this.scrollTarget = null;
    this.track.style.display = 'none';
  }

  private findScrollableChild(root: HTMLElement): HTMLElement | null {
    const all = Array.from(root.querySelectorAll<HTMLElement>('*'));

    const overflowing = all.filter(candidate => {
      const style = getComputedStyle(candidate);
      const hasOverflowX = style.overflowX === 'auto' || style.overflowX === 'scroll';
      return hasOverflowX && candidate.scrollWidth > candidate.clientWidth + 1;
    });

    if (overflowing.length === 0) return null;

    // entre os que realmente estouram, o de maior scrollWidth costuma ser o
    // wrapper principal (cabeçalho + corpo), nao um sub-elemento interno
    return overflowing.reduce((widest, candidate) =>
      candidate.scrollWidth > widest.scrollWidth ? candidate : widest
    );
  }

  private updatePosition(): void {
    if (!this.scrollTarget) return;

    const rect = this.el.nativeElement.getBoundingClientRect();
    const hasOverflow = this.scrollTarget.scrollWidth > this.scrollTarget.clientWidth + 1;
    const visibleBottom = Math.min(rect.bottom, window.innerHeight);
    const visibleTop = Math.max(rect.top, 0);
    const canPlaceBar = hasOverflow && visibleBottom > visibleTop && rect.bottom > 0 && rect.top < window.innerHeight;

    if (!canPlaceBar) {
      this.track.style.display = 'none';
      return;
    }

    this.track.style.display = 'block';
    this.track.style.top = `${Math.max(visibleTop, visibleBottom - 16)}px`;
    this.track.style.left = `${rect.left}px`;
    this.track.style.width = `${rect.width}px`;

    this.updateThumb();
  }

  private updateThumb(): void {
    if (!this.scrollTarget || this.track.style.display === 'none') return;

    const trackWidth = this.track.clientWidth;
    const scrollWidth = this.scrollTarget.scrollWidth;
    const clientWidth = this.scrollTarget.clientWidth;
    const maxScroll = Math.max(scrollWidth - clientWidth, 1);

    const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 30);
    const maxThumbTravel = Math.max(trackWidth - thumbWidth, 1);
    const thumbLeft = (this.scrollTarget.scrollLeft / maxScroll) * maxThumbTravel;

    this.thumb.style.width = `${thumbWidth}px`;
    this.thumb.style.transform = `translateX(${thumbLeft}px)`;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  ngOnDestroy(): void {
    this.thumb?.removeEventListener('pointerdown', this.onThumbPointerDown);
    this.thumb?.removeEventListener('pointermove', this.onThumbPointerMove);
    this.thumb?.removeEventListener('pointerup', this.onThumbPointerUp);
    this.thumb?.removeEventListener('pointercancel', this.onThumbPointerUp);
    this.track?.removeEventListener('pointerdown', this.onTrackPointerDown);
    this.scrollTarget?.removeEventListener('scroll', this.onTableScroll);
    window.removeEventListener('scroll', this.onWindowScrollOrResize, true);
    window.removeEventListener('resize', this.onWindowScrollOrResize);
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.track?.remove();
  }
}
